import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  api,
  JourneyCompareFunnel,
  JourneyExitMixRow,
  JourneyFunnelsCompare,
} from "@/lib/api";
import {
  JOURNEY_WINDOW_OPTIONS,
  JourneyWindowId,
  journeyWindowParams,
} from "@/lib/journeyWindow";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InfoTip } from "@/components/ui/tooltip";
import StatsExplainer from "@/components/StatsExplainer";
import JourneyEntryGroupsSplit from "@/components/JourneyEntryGroupsSplit";
import { cn } from "@/lib/utils";
import { PROFILE_LABELS_PL, type MetricTipId } from "@/lib/metricsGlossary";

type FunnelCategory = "traffic" | "visitor" | "entry" | "version" | "via" | "exit";

const CATEGORY_OPTIONS: { id: FunnelCategory; label: string; tipId?: MetricTipId }[] = [
  { id: "traffic", label: "Skąd przyszli", tipId: "traffic_filter" },
  { id: "visitor", label: "Nowi vs powracający", tipId: "visitor_filter" },
  { id: "entry", label: "Strona wejścia" },
  { id: "via", label: "Z którego linka", tipId: "via_filter" },
  { id: "version", label: "Wersja strony (test)", tipId: "version_filter" },
  { id: "exit", label: "Jak kończą wizytę" },
];

const STEP_META: Record<string, { label: string; tipId: MetricTipId }> = {
  entry: { label: "Wejście", tipId: "funnel_entry" },
  content: { label: "Treść", tipId: "funnel_content" },
  cta: { label: "Przycisk CTA", tipId: "funnel_cta" },
  checkout: { label: "Koszyk", tipId: "funnel_checkout" },
  purchase: { label: "Zakup", tipId: "funnel_purchase_path" },
};

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "-";
  return `${Math.round(v * 1000) / 10}%`;
}

function pickFunnels(
  data: JourneyFunnelsCompare | undefined,
  category: FunnelCategory,
): JourneyCompareFunnel[] {
  if (!data) return [];
  if (category === "traffic") return data.by_traffic ?? [];
  if (category === "visitor") return data.by_visitor ?? [];
  if (category === "entry") return data.by_entry ?? [];
  if (category === "version") return data.by_version ?? [];
  if (category === "via") return data.by_via ?? [];
  return [];
}

function biggestDropInsight(funnels: JourneyCompareFunnel[]): string {
  const ready = funnels.filter((f) => f.enough_data && f.drops?.length);
  if (!ready.length) {
    return "Za mało wizyt w grupach, żeby wskazać największą ucieczkę (min. 15 na lejek).";
  }
  let best: {
    label: string;
    from: string;
    to: string;
    pct: number;
    n: number;
  } | null = null;
  let bestKeep: { label: string; from: string; to: string; pct: number } | null = null;

  for (const f of ready) {
    for (const d of f.drops) {
      if (!best || d.pct > best.pct) {
        best = {
          label: f.label,
          from: d.from,
          to: d.to,
          pct: d.pct,
          n: d.n,
        };
      }
      const keepPct = 1 - d.pct;
      if (!bestKeep || keepPct > bestKeep.pct) {
        bestKeep = {
          label: f.label,
          from: d.from,
          to: d.to,
          pct: keepPct,
        };
      }
    }
  }
  if (!best) return "";
  const fromL = STEP_META[best.from]?.label ?? best.from;
  const toL = STEP_META[best.to]?.label ?? best.to;
  let line = `Największa ucieczka: ${best.label} na ${fromL} → ${toL} (−${pct(best.pct)}, ${best.n} wizyt).`;
  if (bestKeep && bestKeep.label !== best.label) {
    const kf = STEP_META[bestKeep.from]?.label ?? bestKeep.from;
    const kt = STEP_META[bestKeep.to]?.label ?? bestKeep.to;
    line += ` Najlepiej trzyma: ${bestKeep.label} na ${kf} → ${kt}.`;
  }
  return line;
}

export default function JourneyFunnels() {
  const [period, setPeriod] = useState<JourneyWindowId>("7");
  const [category, setCategory] = useState<FunnelCategory>("traffic");

  const query = useQuery({
    queryKey: ["site-analytics-journey-funnels", period],
    queryFn: () => api.siteAnalyticsJourney(journeyWindowParams(period)),
    retry: false,
  });

  const compare = query.data?.funnels_compare;
  const funnels = useMemo(() => pickFunnels(compare, category), [compare, category]);
  const insight = useMemo(
    () => (category === "exit" ? "" : biggestDropInsight(funnels)),
    [category, funnels],
  );

  return (
    <div className="grid gap-5">
      <StatsExplainer variant="lejki" />

      <div className="flex flex-wrap items-center gap-2">
        {JOURNEY_WINDOW_OPTIONS.map((opt) => (
          <Button
            key={opt.id}
            size="sm"
            variant={opt.id === period ? "default" : "outline"}
            onClick={() => setPeriod(opt.id)}
          >
            {opt.label}
          </Button>
        ))}
        <span className="ml-2 text-xs text-muted-foreground">
          {query.data?.period?.label_pl ?? "czas PL"}
          {query.data?.total_sessions != null ? ` · ${query.data.total_sessions} wizyt` : ""}
        </span>
      </div>

      {query.data?.ok && (
        <JourneyEntryGroupsSplit channels={query.data.acquisition_channels} />
      )}

      {compare && (compare.easycart_paid_orders != null || compare.purchase_ssot) && (
        <p className="text-sm text-muted-foreground">
          Zakup w lejkach = opłacone zamówienia EasyCart: {compare.easycart_paid_orders ?? 0}
          {(compare.easycart_purchase_gap ?? 0) > 0
            ? ` · +${compare.easycart_purchase_gap} bez śladu na stronie (dopisane do „Inne”)`
            : ""}
          {(compare.journey_purchase_overcount ?? 0) > 0
            ? ` · nasz licznik zgłasza ${compare.journey_purchase_sessions} (nadmiar ${compare.journey_purchase_overcount} dopasowany do EasyCart)`
            : compare.journey_purchase_sessions != null &&
                compare.journey_purchase_sessions !== compare.easycart_paid_orders
              ? ` · na ścieżce: ${compare.journey_purchase_sessions}`
              : ""}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {CATEGORY_OPTIONS.map((opt) => (
          <Button
            key={opt.id}
            size="sm"
            variant={category === opt.id ? "default" : "outline"}
            onClick={() => setCategory(opt.id)}
            className="gap-1"
          >
            {opt.label}
            {opt.tipId && <InfoTip tipId={opt.tipId} />}
          </Button>
        ))}
      </div>

      {query.isLoading && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Wczytywanie lejków…
          </CardContent>
        </Card>
      )}
      {query.isError && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Nie udało się wczytać danych journey.
          </CardContent>
        </Card>
      )}

      {query.data && category !== "exit" && (
        <>
          <div
            className={cn(
              "grid gap-4",
              funnels.length <= 2
                ? "md:grid-cols-2"
                : funnels.length === 3
                  ? "md:grid-cols-3"
                  : "md:grid-cols-2 xl:grid-cols-4",
            )}
          >
            {funnels.map((f, i) => (
              <FunnelColumn key={f.key} funnel={f} index={i} />
            ))}
            {!funnels.length && (
              <Card>
                <CardContent className="py-8 text-sm text-muted-foreground">
                  Brak grup do porównania w tym okresie.
                </CardContent>
              </Card>
            )}
          </div>
          {insight && (
            <p className="rounded-lg border border-border/60 bg-muted/30 px-4 py-3 text-sm leading-relaxed">
              {insight}
              <InfoTip tipId="biggest_drop" className="ml-1 inline-flex align-middle" />
            </p>
          )}
        </>
      )}

      {query.data && category === "exit" && (
        <ExitMixBar rows={compare?.exit_mix ?? []} />
      )}

      {query.data && category !== "exit" && (compare?.exit_mix?.length ?? 0) > 0 && (
        <div className="pt-2">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Jak kończą wizytę (cały okres)
          </p>
          <ExitMixBar rows={compare!.exit_mix} compact />
        </div>
      )}
    </div>
  );
}

function FunnelColumn({ funnel, index }: { funnel: JourneyCompareFunnel; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{funnel.label}</CardTitle>
          <p className="text-xs text-muted-foreground">{funnel.sessions} wizyt</p>
        </CardHeader>
        <CardContent>
          {!funnel.enough_data ? (
            <p className="py-6 text-sm text-muted-foreground">
              Za mało danych (poniżej 15 wizyt).
              <InfoTip tipId="small_sample" className="ml-1 inline-flex align-middle" />
            </p>
          ) : (
            <div className="flex flex-col items-center gap-1">
              {funnel.steps.map((step, i) => {
                const drop = funnel.drops[i];
                const widthPct = Math.max(8, Math.round((step.pct_of_entry || 0) * 100));
                const meta = STEP_META[step.id];
                return (
                  <div key={step.id} className="flex w-full flex-col items-center">
                    <div
                      className="rounded-md bg-primary/85 px-2 py-2 text-center text-primary-foreground shadow-sm transition-[width]"
                      style={{ width: `${widthPct}%`, minWidth: "4.5rem" }}
                    >
                      <div className="flex items-center justify-center gap-0.5 text-[11px] font-medium opacity-90">
                        {meta?.label ?? step.id}
                        {meta && (
                          <InfoTip tipId={meta.tipId} className="text-primary-foreground/80" />
                        )}
                      </div>
                      <div className="text-sm font-semibold tabular-nums">{step.n}</div>
                      <div className="text-[10px] opacity-80">
                        {pct(step.pct_of_entry)} wejść
                        {i > 0 ? ` · ${pct(step.pct_of_prev)} z poprz.` : ""}
                      </div>
                    </div>
                    {drop && (
                      <div className="my-1 text-[11px] text-muted-foreground">
                        ↓ −{drop.n} ({pct(drop.pct)})
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function ExitMixBar({
  rows,
  compact,
}: {
  rows: JourneyExitMixRow[];
  compact?: boolean;
}) {
  const total = rows.reduce((s, r) => s + (r.sessions || 0), 0) || 1;
  return (
    <Card>
      {!compact && (
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Jak kończą wizytę</CardTitle>
          <p className="text-xs text-muted-foreground">Udział profili w okresie</p>
        </CardHeader>
      )}
      <CardContent className={compact ? "pt-4" : undefined}>
        <div className="flex h-8 w-full overflow-hidden rounded-md border border-border/50">
          {rows.map((r, i) => {
            const w = Math.max(0.5, (r.sessions / total) * 100);
            const tones = [
              "bg-emerald-600/80",
              "bg-emerald-500/70",
              "bg-amber-500/70",
              "bg-sky-500/70",
              "bg-violet-500/70",
              "bg-rose-500/70",
              "bg-slate-500/60",
            ];
            return (
              <div
                key={r.profile}
                title={`${PROFILE_LABELS_PL[r.profile] ?? r.profile}: ${r.sessions} (${pct(r.share)})`}
                className={cn("h-full", tones[i % tones.length])}
                style={{ width: `${w}%` }}
              />
            );
          })}
        </div>
        <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
          {rows.map((r) => (
            <li key={r.profile} className="flex items-center justify-between gap-2 text-xs">
              <span className="truncate text-muted-foreground">
                {PROFILE_LABELS_PL[r.profile] ?? r.profile}
              </span>
              <span className="font-mono tabular-nums">
                {pct(r.share)} · {r.sessions}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
